import { DeleteOutlined, EditOutlined, MoreOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Drawer,
  Dropdown,
  Form,
  Input,
  InputNumber,
  Menu,
  Modal,
  Row,
  Select,
  Table,
} from "antd";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import HandleNotification from "common/Notification";
import {
  GET_SALES_CONTRACT_DETAILS_ENDPOINT,
  GET_ALL_NEW_TNA_TASK_DATA,
  GET_TNA_TEMPLATE_RESPONSIBLE_USERS_ENDPOINT,
  NEW_TNA_TEMPLATE_BASE,
  TNA_BY_STYLE_BASE,
  TNA_POS,
  TNA_SALES_CONTRACT,
} from "apiServices/API_ENDPOINTS";
import { getData, postData, putData } from "apiServices/common";

const AddOrUpdateForm = forwardRef(({ isEdit, view }, ref) => {
  const [form] = Form.useForm();
  const [taskForm] = Form.useForm();
  const [substyleForm] = Form.useForm();
  const [poForm] = Form.useForm();
  const navigate = useNavigate();
  const { tnaByStyleID } = useParams();

  const [salesContractList, setSalesContractList] = useState([]);
  const [styleList, setStyleList] = useState([]);
  const [usedStyleIds, setUsedStyleIds] = useState([]);
  const [poList, setPoList] = useState([]);
  const [templateList, setTemplateList] = useState([]);
  const [taskMasterList, setTaskMasterList] = useState([]);
  const [userList, setUserList] = useState([]);
  const [taskRows, setTaskRows] = useState([]);

  const [taskDrawerOpen, setTaskDrawerOpen] = useState(false);
  const [editingTaskIndex, setEditingTaskIndex] = useState(null);
  const [substyleOpen, setSubstyleOpen] = useState(false);
  const [poOpen, setPoOpen] = useState(false);

  const selectedSalesContractId = Form.useWatch("sales_contract_id", form);

  const toMomentDate = (value) => {
    if (!value) return null;
    return moment.isMoment(value) ? value : moment(value);
  };

  // Legacy style rows store 1970-01-01 when PCD was never set.
  const validPcd = (value) => {
    if (!value) return null;
    const date = moment(value);
    return date.isValid() && date.year() > 1970 ? date.format("YYYY-MM-DD") : null;
  };

  const computeDueDate = (days, pcd) => {
    if (!pcd && pcd !== 0) return "";
    if (days === undefined || days === null || days === "") return "";

    const dayNum = Number(days);
    if (Number.isNaN(dayNum)) return "";

    const base = moment(pcd);
    if (!base.isValid()) return "";

    return dayNum >= 0
      ? base.clone().add(dayNum, "days").format("YYYY-MM-DD")
      : base.clone().subtract(Math.abs(dayNum), "days").format("YYYY-MM-DD");
  };

  const computeDueDateMoment = (days, pcd) => {
    const dueDateStr = computeDueDate(days, pcd);
    return dueDateStr ? moment(dueDateStr, "YYYY-MM-DD") : null;
  };

  const computeDaysFromDueDate = (dueDate, pcd) => {
    if (!dueDate || !pcd) return undefined;

    const due = moment(dueDate);
    const base = moment(pcd);

    if (!due.isValid() || !base.isValid()) return undefined;

    return due.startOf("day").diff(base.startOf("day"), "days");
  };

  const mapTemplateTaskToRow = useCallback(
    (item) => {
      const days = Number(item?.days || 0);
      return {
        id: null,
        task_id: item?.task_id || item?.task?.id,
        task_name: item?.task?.title,
        process_id: item?.process_id || item?.process?.id,
        process_name: item?.process?.title,
        relation_with: item?.relation_with || item?.task?.relation_with,
        substyles: [],
        po_ids: [],
        po_names: [],
        days,
        due_date: computeDueDate(days, form.getFieldValue("pcd_date")),
        responsible_person_id: item?.responsible_person_id || item?.responsible_person?.id,
        responsible_person_name: item?.responsible_person?.name || "",
      };
    },
    [form],
  );

  const getSalesContracts = async (reference_no) => {
    const res = await getData(TNA_SALES_CONTRACT, false, {
      per_page: 100,
      ...(reference_no ? { reference_no } : {}),
    });

    if (res?.data?.code === 200) {
      setSalesContractList(res?.data?.data?.data || []);
    }
  };

  const getUsedStyles = useCallback(
    async (salesContractId) => {
      setUsedStyleIds([]);
      if (!salesContractId) return;

      const res = await getData(TNA_BY_STYLE_BASE, false, {
        per_page: 1000,
        sales_contract_id: salesContractId,
      });
      if (res?.data?.code === 200) {
        const rows = res?.data?.data?.data || [];
        setUsedStyleIds(
          rows
            .filter((row) => String(row?.id) !== String(tnaByStyleID))
            .flatMap((row) => (row?.styles?.length ? row.styles.map((style) => style?.id) : [row?.style_id]))
            .map(Number),
        );
      }
    },
    [tnaByStyleID],
  );

  const getSalesContractDetails = useCallback(
    async (salesContractId, selectedStyles = []) => {
      getUsedStyles(salesContractId);
      const endpoint = GET_SALES_CONTRACT_DETAILS_ENDPOINT(salesContractId);
      const res = await getData(endpoint);

      if (res?.data?.code === 200) {
        const info = res?.data?.data;
        const salesContractInfo = info?.sales_contract;

        const styleDetails = info?.style_details || info?.styleDetail || salesContractInfo?.style_details || [];

        const contractPcd = validPcd(salesContractInfo?.pcd_date);
        const normalizedStyles = (styleDetails || []).map((item) => ({
          id: item?.style_info?.id || item?.style_id || item?.id,
          style_no: item?.style_no || item?.style_info?.style_no,
          pcd: validPcd(item?.pcd) || contractPcd,
        }));

        selectedStyles.forEach((style) => {
          if (style?.id && !normalizedStyles.some((item) => item?.id === style?.id)) {
            normalizedStyles.push({ id: style?.id, style_no: style?.style_no, pcd: contractPcd });
          }
        });

        setStyleList(normalizedStyles);

        form.setFieldsValue({
          buyer_id: salesContractInfo?.buyer?.id || salesContractInfo?.buyer_info?.id,
          buyer_name:
            salesContractInfo?.buyer?.short_name ||
            salesContractInfo?.buyer?.name ||
            salesContractInfo?.buyer_info?.name,
          buying_office_id: salesContractInfo?.buying_office?.id || salesContractInfo?.buying_office_info?.id,
          buying_office_name:
            salesContractInfo?.buying_office?.short_name ||
            salesContractInfo?.buying_office?.name ||
            salesContractInfo?.buying_office_info?.name,
        });
      }
    },
    [form, getUsedStyles],
  );

  const getPOs = async (salesContractId, original_po) => {
    if (!salesContractId) {
      setPoList([]);
      return;
    }

    const res = await getData(`${TNA_POS}/${salesContractId}`, false, {
      ...(original_po ? { original_po } : {}),
    });

    if (res?.data?.code === 200) {
      setPoList(res?.data?.data || []);
    }
  };

  const getTemplates = async (title) => {
    const res = await getData(NEW_TNA_TEMPLATE_BASE, false, {
      per_page: 100,
      ...(title ? { title } : {}),
    });
    if (res?.data?.code === 200) {
      setTemplateList(res?.data?.data?.data || []);
    }
  };

  const getTemplateDetails = async (templateId) => {
    const res = await getData(`${NEW_TNA_TEMPLATE_BASE}/${templateId}/show`);
    if (res?.data?.code === 200) {
      const details = res?.data?.data?.tna_details || [];
      setTaskRows(details.map(mapTemplateTaskToRow));
    }
  };

  const getTaskMasterList = async (title) => {
    const res = await getData(GET_ALL_NEW_TNA_TASK_DATA, false, {
      per_page: 1000,
      ...(title ? { title } : {}),
    });

    if (res?.data?.code === 200) {
      setTaskMasterList(res?.data?.data?.data || []);
    }
  };

  const getUsers = async (name) => {
    const res = await getData(GET_TNA_TEMPLATE_RESPONSIBLE_USERS_ENDPOINT, false, {
      per_page: 100,
      ...(name ? { name } : {}),
    });
    if (res?.data?.code === 200) {
      setUserList(res?.data?.data?.data || []);
    }
  };

  const getDetails = useCallback(async () => {
    if (!isEdit || !tnaByStyleID) return;

    const res = await getData(`${TNA_BY_STYLE_BASE}/${tnaByStyleID}/show`);
    if (res?.data?.code === 200) {
      const data = res?.data?.data;
      const salesContractInfo = data?.sales_contract || data?.salesContract;

      if (salesContractInfo?.id) {
        setSalesContractList((prev) => {
          const exists = prev?.some((item) => item?.id === salesContractInfo?.id);
          if (exists) return prev;

          return [
            ...prev,
            {
              id: salesContractInfo?.id,
              reference_no: salesContractInfo?.reference_no,
            },
          ];
        });
      }

      const savedPcd = validPcd(data?.pcd_date);
      const pcd = savedPcd || validPcd(salesContractInfo?.pcd_date);
      const savedStyles = data?.styles?.length ? data.styles : [data?.style].filter(Boolean);

      form.setFieldsValue({
        sales_contract_id: data?.sales_contract_id,
        style_ids: savedStyles.map((style) => style?.id),
        buyer_id: data?.buyer?.id,
        buyer_name: data?.buyer?.short_name || data?.buyer?.name,
        buying_office_id: data?.buying_office?.id,
        buying_office_name: data?.buying_office?.short_name || data?.buying_office?.name,
        pcd_date: toMomentDate(pcd),
        tna_template_id: data?.tna_template_id,
      });

      await Promise.all([
        getSalesContractDetails(data?.sales_contract_id, savedStyles),
        getPOs(data?.sales_contract_id),
      ]);

      const rows = (data?.tna_style_tasks || []).map((item) => ({
        id: item?.id,
        task_id: item?.task_id || item?.task?.id,
        task_name: item?.task?.title,
        process_id: item?.task?.tna_process_id,
        process_name: item?.task?.process?.title,
        relation_with: item?.task?.relation_with,
        substyles: item?.substyles || [],
        po_ids: item?.po_ids || [],
        po_names: item?.po_names || [],
        days: Number(item?.days || 0),
        due_date: (savedPcd && item?.due_date) || computeDueDate(item?.days || 0, pcd),
        responsible_person_id: item?.responsible_person_id,
        responsible_person_name: item?.responsible_person?.name,
      }));

      setTaskRows(rows);
    }
  }, [form, getSalesContractDetails, isEdit, tnaByStyleID]);

  useEffect(() => {
    getSalesContracts();
    getTemplates();
    getTaskMasterList();
    getUsers();
    getDetails();
  }, [getDetails]);

  // A TNA covering several styles starts from the earliest PCD among them.
  const onChangeStyles = (styleIds) => {
    const pcd = styleList
      ?.filter((item) => styleIds?.includes(item?.id) && item?.pcd)
      .map((item) => item.pcd)
      .sort()[0];

    form.setFieldsValue({ pcd_date: toMomentDate(pcd) });
    setTaskRows((prev) =>
      prev.map((row) => ({
        ...row,
        due_date: computeDueDate(row?.days, pcd),
      })),
    );
  };

  const openTaskDrawer = (index = null) => {
    setEditingTaskIndex(index);
    if (index === null) {
      taskForm.resetFields();
      taskForm.setFieldsValue({
        pcd_date: toMomentDate(form.getFieldValue("pcd_date")),
        due_date: null,
      });
    } else {
      const row = taskRows[index];
      taskForm.setFieldsValue({
        ...row,
        pcd_date: toMomentDate(form.getFieldValue("pcd_date")),
        due_date: toMomentDate(row?.due_date),
      });
    }
    setTaskDrawerOpen(true);
  };

  const onTaskSelect = (taskId) => {
    const task = taskMasterList?.find((item) => item?.id === taskId);
    taskForm.setFieldsValue({
      task_name: task?.title,
      process_id: task?.tna_process_id || task?.process?.id,
      process_name: task?.process?.title,
      relation_with: task?.relation_with,
    });
  };

  const submitTask = async () => {
    try {
      const values = await taskForm.validateFields();
      const pcdDate = form.getFieldValue("pcd_date");
      const due_date = values?.due_date
        ? moment(values?.due_date).format("YYYY-MM-DD")
        : computeDueDate(values?.days, pcdDate);

      const normalizedDays =
        values?.days || values?.days === 0 ? Number(values?.days) : computeDaysFromDueDate(values?.due_date, pcdDate);

      const responsible = userList?.find((u) => u?.id === values?.responsible_person_id);

      const rowPayload = {
        ...(editingTaskIndex !== null ? taskRows[editingTaskIndex] : {}),
        task_id: values?.task_id,
        task_name: values?.task_name,
        process_id: values?.process_id,
        process_name: values?.process_name,
        relation_with: values?.relation_with,
        days: normalizedDays,
        due_date,
        responsible_person_id: values?.responsible_person_id,
        responsible_person_name: responsible?.name,
      };

      if (editingTaskIndex === null) {
        setTaskRows((prev) => [...prev, rowPayload]);
      } else {
        setTaskRows((prev) => prev.map((r, i) => (i === editingTaskIndex ? rowPayload : r)));
      }

      setTaskDrawerOpen(false);
      taskForm.resetFields();
    } catch (_e) {}
  };

  const openSubStyle = (index) => {
    setEditingTaskIndex(index);
    const row = taskRows[index];
    substyleForm.setFieldsValue({
      substyles: row?.substyles || [],
      style_ids: form.getFieldValue("style_ids"),
      task_name: row?.task_name,
      process_name: row?.process_name,
      pcd_date: toMomentDate(form.getFieldValue("pcd_date")),
      days: row?.days,
      responsible_person_id: row?.responsible_person_id,
      due_date: row?.due_date,
    });
    setSubstyleOpen(true);
  };

  const submitSubStyle = async () => {
    try {
      const values = await substyleForm.validateFields();
      const due_date = computeDueDate(values?.days, form.getFieldValue("pcd_date"));
      const responsible = userList?.find((u) => u?.id === values?.responsible_person_id);

      setTaskRows((prev) =>
        prev.map((row, i) =>
          i === editingTaskIndex
            ? {
                ...row,
                substyles: values?.substyles || [],
                days: Number(values?.days),
                due_date,
                responsible_person_id: values?.responsible_person_id,
                responsible_person_name: responsible?.name,
              }
            : row,
        ),
      );
      setSubstyleOpen(false);
    } catch (_e) {}
  };

  const openPO = (index) => {
    setEditingTaskIndex(index);
    const row = taskRows[index];
    poForm.setFieldsValue({
      po_ids: row?.po_ids || [],
      style_ids: form.getFieldValue("style_ids"),
      task_name: row?.task_name,
      process_name: row?.process_name,
      pcd_date: toMomentDate(form.getFieldValue("pcd_date")),
      days: row?.days,
      responsible_person_id: row?.responsible_person_id,
      due_date: row?.due_date,
    });
    setPoOpen(true);
  };

  const submitPO = async () => {
    try {
      const values = await poForm.validateFields();
      const due_date = computeDueDate(values?.days, form.getFieldValue("pcd_date"));
      const responsible = userList?.find((u) => u?.id === values?.responsible_person_id);
      const poNames = poList?.filter((po) => values?.po_ids?.includes(po?.id))?.map((po) => po?.original_po);

      setTaskRows((prev) =>
        prev.map((row, i) =>
          i === editingTaskIndex
            ? {
                ...row,
                po_ids: values?.po_ids || [],
                po_names: poNames || [],
                days: Number(values?.days),
                due_date,
                responsible_person_id: values?.responsible_person_id,
                responsible_person_name: responsible?.name,
              }
            : row,
        ),
      );
      setPoOpen(false);
    } catch (_e) {}
  };

  const removeTask = (index) => {
    Modal.confirm({
      title: "Delete this task?",
      onOk() {
        setTaskRows((prev) => prev.filter((_, i) => i !== index));
      },
    });
  };

  const tableMenu = (row, index) => (
    <Menu
      items={[
        {
          key: "edit",
          label: (
            <span onClick={() => openTaskDrawer(index)}>
              <EditOutlined /> Edit
            </span>
          ),
        },
        {
          key: "add",
          label: (
            <span onClick={() => openTaskDrawer(null)}>
              <PlusOutlined /> Add Task
            </span>
          ),
        },
        {
          key: "substyle",
          label: <span onClick={() => openSubStyle(index)}>SubStyle</span>,
        },
        {
          key: "po",
          label: <span onClick={() => openPO(index)}>PO</span>,
        },
        {
          key: "delete",
          label: (
            <span style={{ color: "red" }} onClick={() => removeTask(index)}>
              <DeleteOutlined /> Delete
            </span>
          ),
        },
      ]}
    />
  );

  const taskColumns = [
    { title: "SL", key: "sl", render: (_, __, index) => index + 1 },
    { title: "Task", dataIndex: "task_name", key: "task_name" },
    { title: "Process", dataIndex: "process_name", key: "process_name" },
    { title: "Substyle", key: "substyle", render: (_, row) => (row?.substyles || []).join(", ") },
    { title: "PO", key: "po", render: (_, row) => (row?.po_names || []).join(", ") },
    { title: "Days", dataIndex: "days", key: "days" },
    { title: "Due Date", dataIndex: "due_date", key: "due_date" },
    { title: "Responsible", dataIndex: "responsible_person_name", key: "responsible" },
    {
      title: "Action",
      key: "action",
      width: 80,
      render: (_, row, index) => (
        <Dropdown overlay={tableMenu(row, index)}>
          <MoreOutlined style={{ cursor: "pointer", fontSize: 18 }} />
        </Dropdown>
      ),
    },
  ];

  useImperativeHandle(ref, () => ({
    submit: async () => {
      try {
        const values = await form.validateFields();

        if (!taskRows?.length) {
          HandleNotification("error", "bottomRight", "Please load template tasks or add task", null);
          return;
        }

        const payload = {
          sales_contract_id: values?.sales_contract_id,
          style_ids: values?.style_ids || [],
          buyer_id: values?.buyer_id,
          buying_office_id: values?.buying_office_id,
          pcd_date: values?.pcd_date ? toMomentDate(values?.pcd_date)?.format("YYYY-MM-DD") : null,
          tna_template_id: values?.tna_template_id,
          tna_style_tasks: taskRows.map((row) => ({
            ...(row?.id ? { id: row?.id } : {}),
            task_id: row?.task_id,
            responsible_person_id: row?.responsible_person_id,
            days: Number(row?.days),
            due_date: row?.due_date,
            substyles: row?.substyles || [],
            po_ids: row?.po_ids || [],
          })),
        };

        const response = isEdit
          ? await putData(`${TNA_BY_STYLE_BASE}/${tnaByStyleID}`, payload)
          : await postData(TNA_BY_STYLE_BASE, payload);

        if (response?.code === 200) {
          HandleNotification(
            "success",
            "bottomRight",
            `TNA By Style ${isEdit ? "updated" : "created"} successfully`,
            null,
          );
          if (isEdit) return;
          navigate(`/tanda/update-tna-by-style/${response?.data?.id}`);
          return;
        }

        HandleNotification("error", "bottomRight", "Failed to save TNA By Style", null);
      } catch (_e) {
        HandleNotification("error", "bottomRight", "Please check required fields", null);
      }
    },
  }));

  return (
    <div className="containt-body">
      <Form layout="vertical" form={form}>
        <Card>
          <Row gutter={8}>
            <Col span={6}>
              <Form.Item
                name="sales_contract_id"
                label="Sale Contract"
                rules={[{ required: true, message: "Select Sale Contract" }]}
              >
                <Select
                  size="small"
                  showSearch
                  filterOption={false}
                  optionFilterProp="children"
                  disabled={view}
                  onSearch={getSalesContracts}
                  allowClear
                  onClear={() => {
                    form.setFieldsValue({
                      style_ids: [],
                      buyer_id: undefined,
                      buyer_name: undefined,
                      buying_office_id: undefined,
                      buying_office_name: undefined,
                      pcd_date: undefined,
                    });
                    setStyleList([]);
                    setUsedStyleIds([]);
                    setPoList([]);
                  }}
                  onSelect={(value) => {
                    form.setFieldsValue({ style_ids: [], pcd_date: undefined });
                    getPOs(value);
                    getSalesContractDetails(value);
                  }}
                >
                  {salesContractList?.map((item) => (
                    <Select.Option key={item?.id} value={item?.id}>
                      {item?.reference_no}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={6}>
              <Form.Item
                name="style_ids"
                label="Style(s)"
                rules={[{ required: true, type: "array", min: 1, message: "Select at least one Style" }]}
              >
                <Select
                  size="small"
                  mode="multiple"
                  showSearch
                  allowClear
                  maxTagCount="responsive"
                  optionFilterProp="children"
                  disabled={view || !selectedSalesContractId}
                  filterOption={(input, option) => (option?.children || "").toLowerCase().includes(input.toLowerCase())}
                  onChange={onChangeStyles}
                >
                  {styleList?.map((item) => {
                    const used = usedStyleIds.includes(Number(item?.id));
                    return (
                      <Select.Option key={item?.id} value={item?.id} disabled={used}>
                        {used ? `${item?.style_no} (TNA added)` : item?.style_no}
                      </Select.Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>

            <Col span={4}>
              <Form.Item name="buyer_name" label="Buyer">
                <Input size="small" disabled />
              </Form.Item>
              <Form.Item name="buyer_id" hidden>
                <Input />
              </Form.Item>
            </Col>

            <Col span={4}>
              <Form.Item name="buying_office_name" label="Buying Office">
                <Input size="small" disabled />
              </Form.Item>
              <Form.Item name="buying_office_id" hidden>
                <Input />
              </Form.Item>
            </Col>

            <Col span={4}>
              <Form.Item name="pcd_date" label="PCD">
                <DatePicker size="small" style={{ width: "100%" }} disabled format="YYYY-MM-DD" />
              </Form.Item>
            </Col>

            <Col span={6}>
              <Form.Item
                name="tna_template_id"
                label="Template"
                rules={[{ required: true, message: "Select Template" }]}
              >
                <Select
                  size="small"
                  showSearch
                  optionFilterProp="children"
                  disabled={view}
                  onSearch={getTemplates}
                  onSelect={getTemplateDetails}
                >
                  {templateList?.map((item) => (
                    <Select.Option key={item?.id} value={item?.id}>
                      {item?.title}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card title="Task List" style={{ marginTop: 12 }}>
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            style={{ marginBottom: 12 }}
            onClick={() => openTaskDrawer(null)}
          >
            Add Task
          </Button>
          <Table
            rowKey={(row, idx) => `${row?.task_id}-${idx}`}
            columns={taskColumns}
            dataSource={taskRows}
            pagination={false}
          />
        </Card>
      </Form>

      <Drawer
        title={editingTaskIndex === null ? "Add Task" : "Edit Task"}
        placement="right"
        open={taskDrawerOpen}
        onClose={() => setTaskDrawerOpen(false)}
        width={520}
      >
        <Form form={taskForm} layout="vertical">
          <Row gutter={8}>
            <Col span={12}>
              <Form.Item name="task_name" hidden>
                <Input />
              </Form.Item>
              <Form.Item name="task_id" label="Task" rules={[{ required: true, message: "Task is required" }]}>
                <Select
                  showSearch
                  optionFilterProp="children"
                  onSearch={getTaskMasterList}
                  onSelect={onTaskSelect}
                  size="small"
                >
                  {taskMasterList?.map((item) => (
                    <Select.Option key={item?.id} value={item?.id}>
                      {item?.title}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="process_id" hidden>
                <Input />
              </Form.Item>
              <Form.Item name="process_name" label="Process">
                <Input size="small" disabled />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="pcd_date" label="PCD">
                <DatePicker size="small" style={{ width: "100%" }} disabled format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="relation_with" label="Relation With">
                <Input size="small" disabled />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="days"
                label="Days"
                rules={[
                  { required: true, message: "Days is required" },
                  {
                    validator: (_, value) => {
                      if (value === 0 || value || value === -0) return Promise.resolve();
                      return Promise.reject(new Error("Days must be numeric"));
                    },
                  },
                ]}
              >
                <InputNumber
                  size="small"
                  style={{ width: "100%" }}
                  onChange={(value) => {
                    const dueDateValue = computeDueDateMoment(value, form.getFieldValue("pcd_date"));
                    taskForm.setFieldsValue({ due_date: dueDateValue });
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="responsible_person_id"
                label="Responsible"
                rules={[{ required: true, message: "Select responsible person" }]}
              >
                <Select size="small" showSearch optionFilterProp="children" onSearch={getUsers}>
                  {userList?.map((item) => (
                    <Select.Option key={item?.id} value={item?.id}>
                      {item?.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="due_date" label="Due Date">
                <DatePicker
                  size="small"
                  style={{ width: "100%" }}
                  format="YYYY-MM-DD"
                  onChange={(date) => {
                    const days = computeDaysFromDueDate(date, form.getFieldValue("pcd_date"));
                    taskForm.setFieldsValue({ days });
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={8}>
            <Col span={12}>
              <Button type="primary" block size="small" onClick={submitTask}>
                {editingTaskIndex === null ? "Add" : "Update"}
              </Button>
            </Col>
            <Col span={12}>
              <Button block size="small" danger onClick={() => setTaskDrawerOpen(false)}>
                Cancel
              </Button>
            </Col>
          </Row>
        </Form>
      </Drawer>

      <Modal title="SubStyle" open={substyleOpen} onOk={submitSubStyle} onCancel={() => setSubstyleOpen(false)}>
        <Form form={substyleForm} layout="vertical">
          <Form.Item name="substyles" label="Substyle">
            <Select mode="tags" size="small" placeholder="Type and press enter for substyle" />
          </Form.Item>
          <Form.Item name="style_ids" label="Style(s)">
            <Select
              size="small"
              mode="multiple"
              showSearch
              optionFilterProp="children"
              disabled
              filterOption={(input, option) => (option?.children || "").toLowerCase().includes(input.toLowerCase())}
            >
              {styleList?.map((item) => (
                <Select.Option key={item?.id} value={item?.id}>
                  {item?.style_no}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="task_name" label="Task">
            <Input size="small" disabled />
          </Form.Item>
          <Form.Item name="process_name" label="Process">
            <Input size="small" disabled />
          </Form.Item>
          <Form.Item name="pcd_date" label="PCD">
            <DatePicker size="small" style={{ width: "100%" }} disabled format="YYYY-MM-DD" />
          </Form.Item>
          <Form.Item name="days" label="Days" rules={[{ required: true, message: "Days is required" }]}>
            <InputNumber size="small" style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="responsible_person_id"
            label="Responsible"
            rules={[{ required: true, message: "Select responsible person" }]}
          >
            <Select size="small" showSearch onSearch={getUsers} optionFilterProp="children">
              {userList?.map((item) => (
                <Select.Option key={item?.id} value={item?.id}>
                  {item?.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Due Date">
            <Input
              size="small"
              disabled
              value={computeDueDate(substyleForm.getFieldValue("days"), form.getFieldValue("pcd_date"))}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal title="PO" open={poOpen} onOk={submitPO} onCancel={() => setPoOpen(false)}>
        <Form form={poForm} layout="vertical">
          <Form.Item name="po_ids" label="PO">
            <Select
              mode="multiple"
              size="small"
              showSearch
              onSearch={(v) => getPOs(form.getFieldValue("sales_contract_id"), v)}
              optionFilterProp="children"
            >
              {poList?.map((item) => (
                <Select.Option key={item?.id} value={item?.id}>
                  {item?.original_po}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="style_ids" label="Style(s)">
            <Select
              size="small"
              mode="multiple"
              showSearch
              disabled
              optionFilterProp="children"
              filterOption={(input, option) => (option?.children || "").toLowerCase().includes(input.toLowerCase())}
            >
              {styleList?.map((item) => (
                <Select.Option key={item?.id} value={item?.id}>
                  {item?.style_no}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="task_name" label="Task">
            <Input size="small" disabled />
          </Form.Item>
          <Form.Item name="process_name" label="Process">
            <Input size="small" disabled />
          </Form.Item>
          <Form.Item name="pcd_date" label="PCD">
            <DatePicker size="small" style={{ width: "100%" }} disabled format="YYYY-MM-DD" />
          </Form.Item>
          <Form.Item name="days" label="Days" rules={[{ required: true, message: "Days is required" }]}>
            <InputNumber size="small" style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="responsible_person_id"
            label="Responsible"
            rules={[{ required: true, message: "Select responsible person" }]}
          >
            <Select size="small" showSearch onSearch={getUsers} optionFilterProp="children">
              {userList?.map((item) => (
                <Select.Option key={item?.id} value={item?.id}>
                  {item?.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Due Date">
            <Input
              size="small"
              disabled
              value={computeDueDate(poForm.getFieldValue("days"), form.getFieldValue("pcd_date"))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
});

export default AddOrUpdateForm;
